import { z } from 'zod'
import { AsyncFn, IsoDateString, isNullishOrEmpty, minifyUrl } from '../../common'
import { guidSchema } from '../../contracts/_common'
import { APPOINTMENT_TYPES } from '../Appointments/Appointments'
import { CompanyGuidContainer, ForCompany } from '../Company/Company'
import { bzOptional } from '../common-schemas'
import { LinkedJobMetadata } from '../LinkedJobs/LinkedJobs'
import { MinimalUser } from '../Users/User'

export const appointmentChecklistAssociationRuleSchema = z.object({
  jobType: bzOptional(
    z.object({
      jobTypeGuid: guidSchema,
      name: z.string(),
    }),
  ),
  appointmentType: bzOptional(z.enum(APPOINTMENT_TYPES)),
  required: bzOptional(z.boolean()),
})

type AppointmentChecklistAssociationRule = z.infer<typeof appointmentChecklistAssociationRuleSchema>
export type AppointmentChecklistJobTypeCondition = NonNullable<AppointmentChecklistAssociationRule['jobType']>

const APPOINTMENT_CHECKLIST_ITEM_TYPE = [
  'CHECKBOX',
  'TEXT',
  'PASS_FAIL_FLAG',
  'YES_NO',
  'INPUT',
  'SIGNATURE',
  'PHOTO',
] as const

export type AppointmentChecklistItemType = (typeof APPOINTMENT_CHECKLIST_ITEM_TYPE)[number]

export const APPOINTMENT_CHECKLIST_ITEM_TYPE_CONFIG = {
  CHECKBOX: {
    label: 'Checkbox',
    options: ['Yes', 'No'],
  },
  TEXT: {
    label: 'Long Answer',
    options: undefined,
  },
  INPUT: {
    label: 'Short Answer',
    options: undefined,
  },
  PASS_FAIL_FLAG: {
    label: 'Pass/Fail/Flag',
    options: ['Pass', 'Fail', 'Flag'],
  },
  YES_NO: {
    label: 'Yes/No',
    options: ['Yes', 'No', 'N/A'],
  },
  SIGNATURE: {
    label: 'Signature',
    options: undefined,
  },
  PHOTO: {
    label: 'Photo Upload',
    options: undefined,
  },
} satisfies Record<AppointmentChecklistItemType, { label: string; options: string[] | undefined }>

// You can configure a checklist item to have a photo attached. You can either set it to not allow ("NONE"), allow it as
// optional ("OPTIONAL"), or require it ("REQUIRED").
export const APPOINTMENT_CHECKLIST_ATTACHED_PHOTO_SETTINGS = ['NONE', 'OPTIONAL', 'REQUIRED'] as const
export type AppointmentChecklistAttachedPhotoSetting = (typeof APPOINTMENT_CHECKLIST_ATTACHED_PHOTO_SETTINGS)[number]

// An "input" type is a field that isn't a title or a dual column section. AKA, an item that you can actually fill out
export const appointmentChecklistInputSchema = z.object({
  type: z.enum(APPOINTMENT_CHECKLIST_ITEM_TYPE),
  // Optional because of backwards compatibility. We don't really use this field meaningfully. We really only use it for
  // the react element keys. When we load up the checklist we fill in some temporary keys.
  checklistItemGuid: bzOptional(guidSchema),
  name: z.string(),
  notRequired: bzOptional(z.boolean()),
  helperText: bzOptional(z.string()),
  associatedPhotoSetting: bzOptional(z.enum(APPOINTMENT_CHECKLIST_ATTACHED_PHOTO_SETTINGS)),
})

export type AppointmentChecklistInput = z.infer<typeof appointmentChecklistInputSchema>

export const appointmentChecklistTitleSchema = z.object({
  checklistItemGuid: bzOptional(guidSchema),
  type: z.literal('TITLE'),
  name: z.string(),
})

export type AppointmentChecklistTitle = z.infer<typeof appointmentChecklistTitleSchema>

// All a dual column section has is two arrays of items
export const appointmentChecklistDualColumnSectionSchema = z.object({
  checklistItemGuid: bzOptional(guidSchema),
  type: z.literal('DUAL_SECTION'),
  left: z.array(appointmentChecklistInputSchema),
  right: z.array(appointmentChecklistInputSchema),
})

export type AppointmentChecklistDualColumnSection = z.infer<typeof appointmentChecklistInstanceDualColumnSectionSchema>

// An item is the input items + titles + dual column sections.
export const appointmentChecklistItemSchema = z.discriminatedUnion('type', [
  appointmentChecklistInputSchema,
  appointmentChecklistTitleSchema,
  appointmentChecklistDualColumnSectionSchema,
])

export type AppointmentChecklistItem = z.infer<typeof appointmentChecklistItemSchema>

// Checks if the provided AppointmentChecklistItem is an 'input' type (AppointmentChecklistInput)
export const isAppointmentChecklistInputItem = (item: AppointmentChecklistItem): item is AppointmentChecklistInput =>
  APPOINTMENT_CHECKLIST_ITEM_TYPE.includes(item.type as AppointmentChecklistItemType)

export const appointmentChecklistSchema = z.object({
  appointmentChecklistGuid: guidSchema,
  name: z.string(),
  associationRules: z.array(appointmentChecklistAssociationRuleSchema),
  items: z.array(appointmentChecklistItemSchema),
  customerFacingByDefault: bzOptional(z.boolean()),
})

export type AppointmentChecklist = z.infer<typeof appointmentChecklistSchema>

export const appointmentChecklistDeleteRequestSchema = z.object({
  appointmentChecklistGuid: guidSchema,
})

export type AppointmentChecklistDeleteRequest = z.infer<typeof appointmentChecklistDeleteRequestSchema>

export const appointmentChecklistInstanceDeleteRequestSchema = z.object({
  appointmentChecklistInstanceGuid: guidSchema,
})

export type AppointmentChecklistInstanceDeleteRequest = z.infer<typeof appointmentChecklistInstanceDeleteRequestSchema>

export const associatedAppointmentChecklistInstancesCreatorRequestSchema = z.object({
  jobAppointmentGuid: guidSchema,
  jobTypeGuid: guidSchema,
  appointmentType: z.enum(APPOINTMENT_TYPES),
})

export type AssociatedAppointmentChecklistInstancesCreatorRequest = z.infer<
  typeof associatedAppointmentChecklistInstancesCreatorRequestSchema
>

export const appointmentChecklistAssociationsMatch = (
  request: AssociatedAppointmentChecklistInstancesCreatorRequest,
  associationRules: AppointmentChecklistAssociationRule[],
) => {
  for (const associationRule of associationRules) {
    const matchesAppointmentType =
      !associationRule.appointmentType || associationRule.appointmentType === request.appointmentType
    const matchesJobClass = !associationRule.jobType || associationRule.jobType.jobTypeGuid === request.jobTypeGuid
    if (matchesAppointmentType && matchesJobClass) {
      return true
    }
  }
  return false
}

// Instance of a checklist (denormalized config + response)
const appointmentChecklistInstanceInputItemSchema = appointmentChecklistInputSchema.extend({
  // The responses are all strings
  response: bzOptional(z.string()),
  // An input can have an associated photo. The config determines if we show the controls for uploading a photo in the
  // UI
  associatedPhotoCdnUrl: bzOptional(z.string()),
})

export type AppointmentChecklistInstanceInputItem = z.infer<typeof appointmentChecklistInstanceInputItemSchema>

// We need to make a specific schema for the dual column section because we need to specify that the sub-items are
// instance items (have responses).
const appointmentChecklistInstanceDualColumnSectionSchema = z.object({
  checklistItemGuid: bzOptional(guidSchema), // same as above
  type: z.literal('DUAL_SECTION'),
  left: z.array(appointmentChecklistInstanceInputItemSchema),
  right: z.array(appointmentChecklistInstanceInputItemSchema),
})

// Same data as a checklist item, but add an optional response
const appointmentChecklistInstanceItemSchema = z.discriminatedUnion('type', [
  appointmentChecklistInstanceInputItemSchema,
  // Title "instance" is the same as the config because it's not an editable field.
  appointmentChecklistTitleSchema,
  appointmentChecklistInstanceDualColumnSectionSchema,
])

export type AppointmentChecklistInstanceItem = z.infer<typeof appointmentChecklistInstanceItemSchema>

export const appointmentChecklistInstanceChecklistSchema = z.object({
  name: z.string(),
  items: z.array(appointmentChecklistInstanceItemSchema),
  adHoc: bzOptional(z.boolean()),
})

export type AppointmentChecklistInstanceChecklist = z.infer<typeof appointmentChecklistInstanceChecklistSchema>

export const writableAppointmentChecklistInstanceSchema = z.object({
  appointmentChecklistInstanceGuid: guidSchema,
  appointmentChecklistGuid: guidSchema,
  jobAppointmentGuid: guidSchema,
  lastEditorUserGuid: bzOptional(guidSchema),
  checklist: appointmentChecklistInstanceChecklistSchema,
  adHoc: bzOptional(z.boolean()),
  instanceNumber: z.number().int().min(1).optional().default(1),
  label: z.string().max(100).nullable().optional(),
})

export type WritableAppointmentChecklistInstance = z.infer<typeof writableAppointmentChecklistInstanceSchema>
export type AppointmentChecklistInstance = WritableAppointmentChecklistInstance & {
  updatedAt: IsoDateString
  lastEditor?: MinimalUser
}

export type LinkedAppointmentChecklistInstance = AppointmentChecklistInstance & {
  linkedJobInfo: LinkedJobMetadata
}

export type AppointmentChecklistsReader = AsyncFn<CompanyGuidContainer, AppointmentChecklist[]>
export type AppointmentChecklistUpserter = AsyncFn<ForCompany<AppointmentChecklist>>
export type AppointmentChecklistDeleter = AsyncFn<ForCompany<AppointmentChecklistDeleteRequest>>
export type AppointmentChecklistInstanceDeleter = AsyncFn<AppointmentChecklistInstanceDeleteRequest>

export type AssociatedAppointmentChecklistInstancesCreator = AsyncFn<
  ForCompany<AssociatedAppointmentChecklistInstancesCreatorRequest>,
  AppointmentChecklistInstance[]
>
export type AppointmentChecklistInstanceBulkUpserter = AsyncFn<
  WritableAppointmentChecklistInstance[],
  AppointmentChecklistInstance[]
>

export const checklistInfoForAppointmentReaderRequestSchema =
  associatedAppointmentChecklistInstancesCreatorRequestSchema.and(
    z.object({
      jobAppointmentGuid: guidSchema,
    }),
  )

export type ChecklistInfoForAppointmentReaderRequestData = z.infer<
  typeof checklistInfoForAppointmentReaderRequestSchema
>
export type ChecklistInfoForAppointmentReaderResponse = {
  instances: AppointmentChecklistInstance[]
  linkedInstances: LinkedAppointmentChecklistInstance[]
  numAssociatedChecklists: number
}

export const checklistInfoForJobReaderRequestSchema = z.object({
  jobGuid: guidSchema,
})

export type ChecklistInfoForJobReaderRequestData = z.infer<typeof checklistInfoForJobReaderRequestSchema>

export type ChecklistInfoForAppointmentReader = AsyncFn<
  ForCompany<ChecklistInfoForAppointmentReaderRequestData>,
  ChecklistInfoForAppointmentReaderResponse
>

export type VisitChecklistJobMetadata = {
  job: {
    jobGuid?: string
    jobTypeName: string
    displayId?: number
  }
  location: {
    locationGuid?: string
    address: {
      line1: string
    }
  }
  visit: {
    appointmentGuid: string
    appointmentType: string
    appointmentDate: IsoDateString
  }
}
export type WithVisitChecklistJobMetadata<T> = T & VisitChecklistJobMetadata

export type ChecklistInfoForJobReaderResponse = {
  instances: WithVisitChecklistJobMetadata<AppointmentChecklistInstance>[]
  linkedInstances: WithVisitChecklistJobMetadata<LinkedAppointmentChecklistInstance>[]
}

export type ChecklistInfoForJobReader = AsyncFn<
  ForCompany<ChecklistInfoForJobReaderRequestData>,
  ChecklistInfoForJobReaderResponse
>

export const checklistItemHasRequiredData = (item: AppointmentChecklistInstanceInputItem) => {
  // Check for normal inputs
  if (!item.notRequired && !item.response) {
    return false
  }
  // Check if we have any required associated photos that aren't present. We can't have associated photos with photo
  // inputs, so don't check if it's a photo.
  if (item.type !== 'PHOTO') {
    // If there is a required associated photo and it's not present, the checklist is not complete yet
    if (item.associatedPhotoSetting === 'REQUIRED' && !item.associatedPhotoCdnUrl) {
      return false
    }
  }

  return true
}

export const checklistHasAtLeastOneAnswer = (items: AppointmentChecklistInstanceChecklist['items']) => {
  for (const checklistItem of items) {
    if (checklistItem.type === 'TITLE') {
      continue
    }

    if (checklistItem.type === 'DUAL_SECTION') {
      if (!checklistHasAtLeastOneAnswer(checklistItem.left) || !checklistHasAtLeastOneAnswer(checklistItem.right)) {
        return false
      }
    }

    if (checklistItem.type === 'INPUT') {
      if (!checklistItem.response) {
        return false
      }
    }

    if ('response' in checklistItem && !isNullishOrEmpty(checklistItem.response)) {
      return true
    }

    if ('associatedPhotoCdnUrl' in checklistItem && !isNullishOrEmpty(checklistItem.associatedPhotoCdnUrl)) {
      return true
    }
  }
  return true
}

export const isChecklistCompleted = (items: AppointmentChecklistInstanceChecklist['items']) => {
  for (const checklistItem of items) {
    if (checklistItem.type === 'TITLE') {
      continue
    }

    if (checklistItem.type === 'DUAL_SECTION') {
      // For dual sections, check both left and right sections
      if (!isChecklistCompleted(checklistItem.left) || !isChecklistCompleted(checklistItem.right)) {
        return false
      }
      continue
    }

    if (!checklistItemHasRequiredData(checklistItem)) {
      return false
    }
  }
  return true
}

export const isChecklistRequired = (items: AppointmentChecklistInstanceItem[]): boolean =>
  items.some(item => {
    if (item.type === 'TITLE') {
      return false
    }

    if (item.type === 'DUAL_SECTION') {
      // For dual sections, check both left and right sections
      return isChecklistRequired(item.left) || isChecklistRequired(item.right)
    }

    return !item.notRequired || item.associatedPhotoSetting === 'REQUIRED'
  })

export const createDirectChecklistLink = (accountAppFullUrl: string, appointmentChecklistInstanceGuid: string) =>
  `${accountAppFullUrl}/form/${appointmentChecklistInstanceGuid}`
export const generateChecklistAccountAppLink = (accountAppFullUrl: string, appointmentChecklistInstanceGuid: string) =>
  minifyUrl(createDirectChecklistLink(accountAppFullUrl, appointmentChecklistInstanceGuid))

/**
 * Returns the display name for a checklist instance.
 * - If a label is provided and non-empty after trimming, returns "ChecklistName - Label"
 * - If instanceNumber > 1, returns "ChecklistName #N"
 * - Otherwise returns the base checklist name
 */
export const getInstanceDisplayName = (
  checklistName: string,
  instanceNumber: number,
  label: string | null | undefined,
): string => {
  // Use label.trim() to handle whitespace-only labels
  if (label && label.trim()) return `${checklistName} - ${label.trim()}`
  // Show #${instanceNumber} for instances after the first
  if (instanceNumber > 1) return `${checklistName} #${instanceNumber}`
  return checklistName
}

// Request schema for adding a new checklist instance
export const addAppointmentChecklistInstanceRequestSchema = z.object({
  jobAppointmentGuid: guidSchema,
  appointmentChecklistGuid: guidSchema,
  label: z.string().max(100).optional(),
})
export type AddAppointmentChecklistInstanceRequestData = z.infer<typeof addAppointmentChecklistInstanceRequestSchema>

// Service type for adding a new checklist instance
export type AddAppointmentChecklistInstance = AsyncFn<
  ForCompany<AddAppointmentChecklistInstanceRequestData>,
  AppointmentChecklistInstance
>
